/**
 * FieldSearchPanel — поиск поля на карте по имени (AK-378, Feature 1).
 *
 * Анкорится слева от RightRail (right:56, top:0) — визуально «выезжает» из-под
 * кнопки-лупы. Внутри: DS `Input` (debounced) → `ScrollArea` со списком
 * совпадений (имя + культура/площадь). Клик по строке →
 *   1. `map.flyTo({ center: [lng,lat], zoom≈15 })` — центрируем по центроиду;
 *   2. `dispatch(SELECT_FIELD)` — жёлтый контур выбранного поля (тот же
 *      highlight-механизм, что и клик по полигону в v2.tsx).
 *
 * Предусловие (farm-scope) проверяет RightRail: панель монтируется только
 * когда `activeScope.type === 'farm'` и есть farm_id. Здесь farmId уже валиден.
 *
 * DS-only: `@/components/ui/{input,scroll-area}`, semantic-токены. Никаких hex.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import type maplibregl from 'maplibre-gl';
import { Search, MapPin } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useMapStore } from '@/lib/map/useMapStoreV2';
import { useFieldSearch, type FieldSearchItem } from '@/lib/map/useFieldSearch';

interface FieldSearchPanelProps {
  map: maplibregl.Map | null;
  farmId: string;
  /** Закрыть панель (снять active с кнопки-лупы). */
  onClose: () => void;
}

/** Debounce простого string-значения (ввод в поиск). */
function useDebounced(value: string, delayMs: number): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

function formatMeta(item: FieldSearchItem): string {
  const parts: string[] = [];
  if (item.current_crop) parts.push(item.current_crop);
  if (item.area_ha != null) parts.push(`${item.area_ha.toFixed(item.area_ha < 10 ? 1 : 0)} га`);
  return parts.join(' · ');
}

export function FieldSearchPanel({ map, farmId, onClose }: FieldSearchPanelProps) {
  const dispatch = useMapStore((s) => s.dispatch);
  const [query, setQuery] = useState('');
  const debounced = useDebounced(query, 250);
  const inputRef = useRef<HTMLInputElement>(null);

  const { data, isFetching, isError } = useFieldSearch(debounced, farmId);
  const results = useMemo(() => data ?? [], [data]);

  // Автофокус при открытии — сразу можно печатать.
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Esc закрывает панель.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const handlePick = (item: FieldSearchItem) => {
    if (item.centroid && map) {
      map.flyTo({
        center: item.centroid,
        zoom: Math.max(map.getZoom(), 15),
        duration: 700,
        essential: true,
      });
    }
    // Highlight выбранного поля (жёлтый контур) — тот же путь, что клик по полю.
    dispatch({ type: 'SELECT_FIELD', fieldId: item.id });
    onClose();
  };

  const trimmed = debounced.trim();
  const showEmpty = trimmed.length > 0 && !isFetching && !isError && results.length === 0;

  return (
    <div
      data-testid="field-search-panel"
      role="dialog"
      aria-label="Поиск поля"
      // Анкор слева от рейла (48px + 16px offset ≈ 56px влево). Адаптивная
      // ширина: до 260px, но не шире доступного места на узких экранах.
      style={{ position: 'absolute', right: 56, top: 0, zIndex: 31, width: 'min(260px, 78vw)' }}
      className="rounded-md border border-border bg-background/95 backdrop-blur-sm shadow-lg overflow-hidden"
    >
      <div className="flex items-center gap-2 border-b border-border px-3 py-2">
        <Search size={16} className="shrink-0 text-muted-foreground" aria-hidden />
        <Input
          ref={inputRef}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Имя поля…"
          data-testid="field-search-input"
          className="h-8 border-0 bg-transparent px-0 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0"
        />
      </div>

      {isError && (
        <div
          data-testid="field-search-error"
          className="px-3 py-3 text-xs text-muted-foreground"
        >
          Не удалось выполнить поиск. Попробуйте ещё раз.
        </div>
      )}

      {!isError && trimmed.length === 0 && (
        <div className="px-3 py-3 text-xs text-muted-foreground">
          Введите имя или часть имени поля.
        </div>
      )}

      {!isError && isFetching && trimmed.length > 0 && (
        <div
          data-testid="field-search-loading"
          className="px-3 py-3 text-xs text-muted-foreground"
        >
          Поиск…
        </div>
      )}

      {showEmpty && (
        <div
          data-testid="field-search-empty"
          className="px-3 py-3 text-xs text-muted-foreground"
        >
          Ничего не найдено по «{trimmed}».
        </div>
      )}

      {!isError && !isFetching && results.length > 0 && (
        <ScrollArea className="max-h-[240px]">
          <ul data-testid="field-search-results" className="py-1">
            {results.map((item) => {
              const meta = formatMeta(item);
              const hasGeom = !!item.centroid;
              return (
                <li key={item.id}>
                  <button
                    type="button"
                    data-testid="field-search-result"
                    onClick={() => handlePick(item)}
                    disabled={!hasGeom}
                    title={hasGeom ? undefined : 'У поля нет контура — нельзя показать на карте'}
                    className={
                      'flex w-full items-center gap-2 px-3 py-2 text-left transition-colors ' +
                      (hasGeom
                        ? 'hover:bg-accent cursor-pointer'
                        : 'cursor-not-allowed opacity-50')
                    }
                  >
                    <MapPin size={14} className="shrink-0 text-muted-foreground" aria-hidden />
                    <span className="min-w-0 flex-1 truncate text-sm text-foreground">
                      {item.name}
                    </span>
                    {meta && (
                      <span className="shrink-0 text-xs text-muted-foreground">{meta}</span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </ScrollArea>
      )}
    </div>
  );
}
